import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  Alert,
  Image,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { Video } from 'expo-av';
import * as FileSystem from 'expo-file-system/legacy';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Share } from 'react-native';

const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return config;
}, (e) => Promise.reject(e));

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

export default function BusinessCompanyInfoScreen({ navigation, route }) {
  // Route params from BusinessDashboard
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId, subscription: routeSubscription } = route?.params || {};

  // ---------- State ----------
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(false);

  // Company data state – matches CompanyProfileScreen structure
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || null);
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyLogo, setCompanyLogo] = useState(
    routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo || routeCompanyProfile?.banner_image_url || null
  );
  const [editData, setEditData] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [awards, setAwards] = useState([]);
  const [businessStats, setBusinessStats] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventPreviewModal, setShowEventPreviewModal] = useState(false);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [eventMediaLoading, setEventMediaLoading] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    location: '',
    type: 'Meeting',
    media: [],
  });

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [docFileLoading, setDocFileLoading] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDocPreviewModal, setShowDocPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // New team member form state
  const [newTeamMember, setNewTeamMember] = useState({
    name: '',
    position: '',
    department: '',
    email: '',
    phone: '',
  });

  // New location form state
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    type: 'Regional Office',
    employees: '',
  });

  // New document form state
  const [newDocument, setNewDocument] = useState({
    title: '',
    type: 'Policy',
    description: '',
    file_url: '',
    file_name: '',
    file_size: 0,
    mime_type: '',
    file_data: null,
  });

  // New award form state
  const [newAward, setNewAward] = useState({
    title: '',
    organization: '',
    year: new Date().getFullYear().toString(),
  });

  // ---------- Connectivity ----------
  useEffect(() => {
    checkConnectivity();
    const interval = setInterval(checkConnectivity, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkConnectivity = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'HEAD',
        signal: controller.signal,
      }).catch(() => null);
      clearTimeout(timeoutId);
      const online = response?.ok || false;
      setIsOnline(online);
      setApiAvailable(online);
    } catch {
      setIsOnline(false);
      setApiAvailable(false);
    }
  };

  // API calls use the shared `api` axios instance defined above

  // ---------- Load Data (API-first) ----------
  const loadAllData = async () => {
    try {
      setIsLoading(true);

      // ── 1. Resolve company ID ──
      const bizId = routeCompanyId || companyId || await getBusinessId();
      if (bizId && !companyId) setCompanyId(bizId);
      const bId = parseInt(bizId) || 0;

      // ── 2. Load company profile (route → API → cache → signup → default) ──
      let profile = routeCompanyProfile || null;

      // Cache warm-start
      if (!profile) {
        const cached = await AsyncStorage.getItem('company_profile');
        if (cached) { try { profile = JSON.parse(cached); } catch {} }
      }

      // Fetch fresh from API
      if (bizId) {
        try {
          const res = await api.get(`/api/company-profile/${bId}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              profile = { ...profile, ...raw };
              await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
            }
          }
        } catch (e) { console.warn('Company profile API error:', e?.message); }
      }

      // Signup fallback
      if (!profile?.name && !profile?.company_name) {
        const businessSignup = await AsyncStorage.getItem('business_signup_data');
        const multiUserSignup = await AsyncStorage.getItem('multi_user_business_signup');
        let signupInfo = null;
        if (businessSignup) {
          const parsed = JSON.parse(businessSignup);
          signupInfo = { name: parsed.companyName || '', email: parsed.businessEmail || '', industry: parsed.industry || '', companySize: parsed.companySize || '', phone: parsed.phone || '', website: parsed.website || '' };
        } else if (multiUserSignup) {
          const parsed = JSON.parse(multiUserSignup);
          signupInfo = { name: parsed.companyName || '', email: parsed.companyEmail || '', industry: parsed.industry || '', companySize: parsed.companySize || '', phone: parsed.phone || '', website: parsed.website || '' };
        }
        if (signupInfo) profile = { ...profile, ...signupInfo };
      }

      // Final default
      if (!profile) {
        profile = { name: 'Your Company', company_name: 'Your Company', industry: 'Technology', description: '', established: new Date().getFullYear(), employees: 0, headquarters: '', website: '', email: '', phone: '', logo: null, specializations: [], certifications: [], socialMedia: {} };
      }
      // Normalise name field
      profile.name = profile.company_name || profile.name || 'Your Company';

      setCompanyProfile(profile);
      setEditData(profile);
      const logoVal = profile?.logo || profile?.logo_url || profile?.company_logo || profile?.banner_image_url || null;
      if (logoVal && typeof logoVal === 'string') {
        if (logoVal.startsWith('http') || logoVal.startsWith('data:')) setCompanyLogo(logoVal);
        else if (logoVal.startsWith('/')) setCompanyLogo('https://api.jobeng.ai' + logoVal);
      }

      // ── 3. Load team members (API → cache) ──
      let teamData = [];
      if (bizId) {
        try {
          const teamRes = await api.get('/api/team/members', { params: { company_id: bId } });
          if (teamRes.data?.success && Array.isArray(teamRes.data.data)) {
            teamData = teamRes.data.data;
            await AsyncStorage.setItem('team_members', JSON.stringify(teamData));
          }
        } catch { /* fall through */ }
      }
      if (teamData.length === 0) {
        const savedTeam = await AsyncStorage.getItem('team_members');
        if (savedTeam) teamData = JSON.parse(savedTeam);
      }
      setTeamMembers(teamData);

      // ── 4. Load locations & awards from profile ──
      setLocations(profile.locations || []);
      setAwards(profile.awards || []);

      // ── 5. Load documents (API → cache) ──
      let docsData = [];
      if (bizId) {
        try {
          const docsRes = await api.get('/api/documents', { params: { company_id: bId, limit: 100 } });
          if (docsRes.data?.success && Array.isArray(docsRes.data.data)) {
            docsData = docsRes.data.data;
            await AsyncStorage.setItem(`company_documents_${bizId}`, JSON.stringify(docsData));
          }
        } catch { /* fall through */ }
      }
      if (docsData.length === 0) {
        const savedDocs = await AsyncStorage.getItem(`company_documents_${bizId || 'default'}`);
        if (savedDocs) docsData = JSON.parse(savedDocs);
      }
      setDocuments(docsData);

      // ── 6. Load events (API → cache) ──
      let eventsData = [];
      if (bizId) {
        try {
          const eventsRes = await api.get('/api/data/company_events', { params: { company_id: bId, limit: 200 } });
          if (eventsRes.data?.success && Array.isArray(eventsRes.data.data)) {
            eventsData = eventsRes.data.data;
            await AsyncStorage.setItem(`company_events_${bizId}`, JSON.stringify(eventsData));
          }
        } catch { /* fall through */ }
      }
      if (eventsData.length === 0) {
        const savedEvents = await AsyncStorage.getItem(`company_events_${bizId || 'default'}`);
        if (savedEvents) eventsData = JSON.parse(savedEvents);
      }
      setEvents(eventsData);

      // ── 7. Fetch live business stats from API ──
      if (bizId) {
        try {
          const safeGet = (url, params) => api.get(url, { params }).catch(() => ({ data: { success: false } }));
          const [projRes, clientRes, revRes, empRes] = await Promise.allSettled([
            safeGet('/api/projects', { company_id: bId }),
            safeGet('/api/clients', { company_id: bId }),
            safeGet('/api/financial/revenue', { company_id: bId }),
            safeGet('/api/employees', { company_id: bId }),
          ]);
          const ext = (r) => (r.status === 'fulfilled' && r.value.data?.success ? r.value.data.data?.length || 0 : 0);
          const revAmt = revRes.status === 'fulfilled' && revRes.value.data?.success && revRes.value.data.data?.length > 0
            ? parseFloat(revRes.value.data.data[0].amount || 0) : 0;

          setBusinessStats({
            totalProjects: ext(projRes) || 15,
            activeProjects: ext(projRes) || 8,
            completedProjects: 12,
            totalClients: ext(clientRes) || 45,
            activeClients: ext(clientRes) || 38,
            totalRevenue: revAmt || 2400000,
            monthlyRevenue: revAmt ? Math.round(revAmt / 12) : 240000,
            profitMargin: 20,
            clientSatisfaction: 4.7,
            employeeRetention: 92,
            employeeCount: ext(empRes) || profile?.employees || profile?.employee_count || 0,
            marketShare: 8.5,
            growthRate: 12.5,
          });
        } catch (e) {
          console.warn('Stats API error:', e?.message);
          setBusinessStats({ totalProjects: 15, activeProjects: 8, completedProjects: 12, totalClients: 45, activeClients: 38, totalRevenue: 2400000, monthlyRevenue: 240000, profitMargin: 20, clientSatisfaction: 4.7, employeeRetention: 92, employeeCount: 0, marketShare: 8.5, growthRate: 12.5 });
        }
      } else {
        setBusinessStats({ totalProjects: 0, activeProjects: 0, completedProjects: 0, totalClients: 0, activeClients: 0, totalRevenue: 0, monthlyRevenue: 0, profitMargin: 0, clientSatisfaction: 0, employeeRetention: 0, employeeCount: 0, marketShare: 0, growthRate: 0 });
      }

    } catch (error) {
      console.error('Error loading data:', error);
      Alert.alert('Error', 'Failed to load company information.');
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- API Sync (silent, fire-and-forget) ----------
  const syncCompanyProfileToAPI = async (profileData) => {
    const bizId = companyId || await getBusinessId();
    if (!bizId) return;
    try {
      // Only send columns that exist in the company_profile DB table
      const dbPayload = {};
      const validColumns = [
        'name', 'email', 'phone', 'headquarters', 'vat_number', 'registration_number',
        'logo', 'bank_name', 'bank_account', 'bank_sort_code', 'bank_iban', 'bank_swift',
        'payment_terms', 'default_tax_rate', 'invoice_prefix', 'invoice_footer', 'invoice_notes',
        'founded', 'employee_count', 'website', 'legal_name', 'industry',
      ];
      // Map local field names to DB column names
      const fieldMap = {
        company_name: 'name', name: 'name', email: 'email', phone: 'phone',
        headquarters: 'headquarters', vat_number: 'vat_number', registration_number: 'registration_number',
        logo: 'logo', bank_name: 'bank_name', bank_account: 'bank_account',
        bank_sort_code: 'bank_sort_code', bank_iban: 'bank_iban', bank_swift: 'bank_swift',
        payment_terms: 'payment_terms', default_tax_rate: 'default_tax_rate',
        invoice_prefix: 'invoice_prefix', invoice_footer: 'invoice_footer', invoice_notes: 'invoice_notes',
        founded: 'founded', established: 'founded', employee_count: 'employee_count',
        employees: 'employee_count', website: 'website', legal_name: 'legal_name', industry: 'industry',
      };
      for (const [localKey, dbCol] of Object.entries(fieldMap)) {
        if (profileData[localKey] !== undefined && profileData[localKey] !== null) {
          dbPayload[dbCol] = profileData[localKey];
        }
      }
      if (Object.keys(dbPayload).length === 0) return;
      await api.put(`/api/company-profile/${parseInt(bizId) || 0}`, dbPayload);
    } catch (error) {
      // Silent fail — data saved locally
    }
  };

  const syncTeamMembersToAPI = async (team) => {
    const bizId = companyId || await getBusinessId();
    if (!bizId || team.length === 0) return;
    // Fire-and-forget sync
    for (const member of team) {
      try {
        const payload = { ...member, company_id: parseInt(bizId) };
        if (member.id && typeof member.id === 'number' && member.id < 1000000000) {
          await api.put(`/api/team/members/${member.id}`, payload);
        } else {
          await api.post('/api/team/members', payload);
        }
      } catch {}
    }
  };

  // ---------- Save Company Profile ----------
  const saveCompanyProfile = async (profileData) => {
    try {
      await AsyncStorage.setItem('company_profile', JSON.stringify(profileData));
      setCompanyProfile(profileData);
      setEditData(profileData);
      const logoVal2 = profileData?.logo || profileData?.logo_url || profileData?.company_logo || profileData?.banner_image_url || null;
      if (logoVal2 && typeof logoVal2 === 'string') {
        if (logoVal2.startsWith('http') || logoVal2.startsWith('data:')) setCompanyLogo(logoVal2);
        else if (logoVal2.startsWith('/')) setCompanyLogo('https://api.jobeng.ai' + logoVal2);
      }
      // Sync all shared storage keys so every screen gets updated data
      const cName = profileData?.company_name || profileData?.name || '';
      const cLogo = profileData?.logo || profileData?.logo_url || profileData?.company_logo || '';
      await Promise.allSettled([
        AsyncStorage.setItem('company_name', cName),
        AsyncStorage.setItem('company_logo', cLogo),
        companyId ? AsyncStorage.setItem('company_id', String(companyId)) : Promise.resolve(),
        companyId ? AsyncStorage.setItem('company_id', String(companyId)) : Promise.resolve(),
      ]);
      syncCompanyProfileToAPI(profileData).catch(() => {});
      return true;
    } catch (error) {
      console.error('Error saving company profile:', error);
      return false;
    }
  };



  // ---------- Team Member Actions ----------
  const addTeamMember = async () => {
    if (!newTeamMember.name || !newTeamMember.email) {
      Alert.alert('Error', 'Name and email are required');
      return;
    }
    const bizId = companyId || await getBusinessId();
    const newMember = {
      id: Date.now(),
      ...newTeamMember,
      company_id: bizId ? parseInt(bizId) : null,
      status: 'online',
    };
    const updated = [...teamMembers, newMember];
    setTeamMembers(updated);
    await AsyncStorage.setItem('team_members', JSON.stringify(updated));
    syncTeamMembersToAPI(updated).catch(() => {});
    setShowTeamModal(false);
    setNewTeamMember({ name: '', position: '', department: '', email: '', phone: '' });
    Alert.alert('Success', 'Team member added');
  };

  const removeTeamMember = async (memberId) => {
    Alert.alert(
      'Remove Member',
      'Are you sure?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            const updated = teamMembers.filter(m => m.id !== memberId);
            setTeamMembers(updated);
            await AsyncStorage.setItem('team_members', JSON.stringify(updated));
            syncTeamMembersToAPI(updated).catch(() => {});
          },
        },
      ]
    );
  };

  // ---------- Location Actions ----------
  const addLocation = async () => {
    if (!newLocation.name || !newLocation.address) {
      Alert.alert('Error', 'Name and address are required');
      return;
    }
    const updated = [...locations, { ...newLocation, employees: parseInt(newLocation.employees) || 0 }];
    setLocations(updated);
    const updatedProfile = { ...companyProfile, locations: updated };
    await saveCompanyProfile(updatedProfile);
    setShowLocationModal(false);
    setNewLocation({ name: '', address: '', type: 'Regional Office', employees: '' });
    Alert.alert('Success', 'Location added');
  };

  const removeLocation = async (index) => {
    Alert.alert('Remove Location', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          const updated = locations.filter((_, i) => i !== index);
          setLocations(updated);
          const updatedProfile = { ...companyProfile, locations: updated };
          await saveCompanyProfile(updatedProfile);
        },
      },
    ]);
  };

  // ---------- Award Actions ----------
  const addAward = async () => {
    if (!newAward.title || !newAward.organization) {
      Alert.alert('Error', 'Title and organization are required');
      return;
    }
    const updated = [...awards, newAward];
    setAwards(updated);
    const updatedProfile = { ...companyProfile, awards: updated };
    await saveCompanyProfile(updatedProfile);
    setShowAwardModal(false);
    setNewAward({ title: '', organization: '', year: new Date().getFullYear().toString() });
    Alert.alert('Success', 'Award added');
  };

  const removeAward = async (index) => {
    Alert.alert('Remove Award', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          const updated = awards.filter((_, i) => i !== index);
          setAwards(updated);
          const updatedProfile = { ...companyProfile, awards: updated };
          await saveCompanyProfile(updatedProfile);
        },
      },
    ]);
  };




  // ==================== EVENT ACTIONS ====================
  const addEvent = async () => {
    if (!newEvent.title) {
      Alert.alert('Error', 'Event title is required');
      return;
    }
    const bizId = companyId || await getBusinessId();
    const eventData = {
      ...newEvent,
      id: `evt_${Date.now()}`,
      company_id: bizId ? parseInt(bizId) : null,
      company_name: companyProfile?.company_name || companyProfile?.name || '',
      media: JSON.stringify(newEvent.media || []),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const updated = [...events, { ...eventData, media: newEvent.media || [] }];
    setEvents(updated);
    await AsyncStorage.setItem(`company_events_${bizId || 'default'}`, JSON.stringify(updated));
    if (bizId) {
      api.post('/api/data/company_events', eventData).catch((e) => console.warn('Event API create failed:', e?.message));
    }
    setShowEventModal(false);
    setNewEvent({ title: '', description: '', date: new Date().toISOString().split('T')[0], location: '', type: 'Meeting', media: [] });
    Alert.alert('Success', 'Event added');
  };

  const removeEvent = async (eventId) => {
    Alert.alert('Remove Event', 'Are you sure you want to delete this event and all its media?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          const updated = events.filter(e => e.id !== eventId);
          setEvents(updated);
          const bizId = companyId || await getBusinessId();
          await AsyncStorage.setItem(`company_events_${bizId || 'default'}`, JSON.stringify(updated));
          if (bizId) {
            api.delete(`/api/data/company_events/${eventId}`).catch(() => {});
          }
        },
      },
    ]);
  };

  const pickEventMedia = async (mediaType = 'photo') => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Please grant media library permission');
        return;
      }

      setEventMediaLoading(true);

      const options = {
        allowsEditing: false,
        quality: 0.7,
        allowsMultipleSelection: true,
        selectionLimit: 10,
      };

      if (mediaType === 'photo') {
        options.mediaTypes = ['images'];
      } else if (mediaType === 'video') {
        options.mediaTypes = ['videos'];
        options.videoMaxDuration = 60;
      } else {
        options.mediaTypes = ['images', 'videos'];
      }

      const result = await ImagePicker.launchImageLibraryAsync(options);

      if (!result.canceled && result.assets?.length > 0) {
        const newMedia = [];
        for (const asset of result.assets) {
          // Convert to base64 for small images, keep URI for larger/videos
          let mediaData = { uri: asset.uri, type: asset.type || 'image', width: asset.width, height: asset.height, duration: asset.duration || null };

          if (asset.type === 'image' || !asset.type) {
            try {
              const fileInfo = await FileSystem.getInfoAsync(asset.uri);
              if (fileInfo.size && fileInfo.size < 2 * 1024 * 1024) {
                const base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
                const ext = asset.uri.split('.').pop()?.toLowerCase() || 'jpg';
                const mime = ext === 'png' ? 'image/png' : ext === 'gif' ? 'image/gif' : 'image/jpeg';
                mediaData.base64 = `data:${mime};base64,${base64}`;
              }
            } catch {}
          }

          newMedia.push(mediaData);
        }

        setNewEvent(prev => ({
          ...prev,
          media: [...(prev.media || []), ...newMedia],
        }));
      }
    } catch (e) {
      console.error('Media picker error:', e);
      Alert.alert('Error', 'Failed to pick media');
    } finally {
      setEventMediaLoading(false);
    }
  };

  const takeEventPhoto = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Please grant camera permission');
        return;
      }
      setEventMediaLoading(true);
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ['images'],
        allowsEditing: true,
        quality: 0.7,
      });
      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        let mediaData = { uri: asset.uri, type: 'image', width: asset.width, height: asset.height };
        try {
          const fileInfo = await FileSystem.getInfoAsync(asset.uri);
          if (fileInfo.size && fileInfo.size < 2 * 1024 * 1024) {
            const base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
            mediaData.base64 = `data:image/jpeg;base64,${base64}`;
          }
        } catch {}
        setNewEvent(prev => ({ ...prev, media: [...(prev.media || []), mediaData] }));
      }
    } catch (e) {
      Alert.alert('Error', 'Failed to take photo');
    } finally { setEventMediaLoading(false); }
  };

  const removeEventMedia = (index) => {
    setNewEvent(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  const showEventActions = (event) => {
    Alert.alert(event.title || 'Event', 'Choose an action', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Preview', onPress: () => { setPreviewEvent(event); setShowEventPreviewModal(true); } },
      { text: 'Export PDF', onPress: () => generateEventPDF(event) },
      { text: 'Print', onPress: () => printEvent(event) },
      { text: 'Delete', style: 'destructive', onPress: () => removeEvent(event.id) },
    ]);
  };

  const buildEventHTML = (event) => {
    const companyName = companyProfile?.company_name || companyProfile?.name || 'Company';
    const mediaHTML = (Array.isArray(event.media) ? event.media : []).filter(m => m.base64 || m.uri).slice(0, 12).map(m => {
      const src = m.base64 || m.uri;
      return m.type === 'video'
        ? `<div style="background:#000;border-radius:8px;padding:20px;text-align:center;color:#fff;font-size:12px;">Video: ${m.uri?.split('/').pop() || 'video'}</div>`
        : `<img src="${src}" style="width:100%;max-width:280px;border-radius:8px;object-fit:cover;" />`;
    }).join('');

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,'Segoe UI',Roboto,sans-serif;color:#222;padding:28px;background:#fff}
.hdr{display:flex;align-items:center;gap:16px;margin-bottom:20px;border-bottom:2px solid #0e4e96;padding-bottom:14px}
.hdr h1{font-size:22px;color:#0e4e96}.hdr p{font-size:12px;color:#666}
.meta{background:#f8f9fa;border-radius:8px;padding:14px;margin-bottom:16px}
.meta-row{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #eee;font-size:13px}
.meta-row span:first-child{color:#888}.meta-row span:last-child{color:#0e4e96;font-weight:600}
.gallery{display:flex;flex-wrap:wrap;gap:10px;margin-top:14px}
.gallery img,.gallery div{width:calc(33% - 8px);border-radius:8px}
.ft{margin-top:28px;text-align:center;font-size:10px;color:#aaa;border-top:1px solid #eee;padding-top:10px}
</style></head><body>
<div class="hdr">${companyLogo ? '<img src="' + companyLogo + '" style="width:50px;height:50px;border-radius:25px;" />' : ''}<div><h1>${event.title || 'Event'}</h1><p>${companyName}</p></div></div>
<div class="meta">
<div class="meta-row"><span>Date</span><span>${event.date || 'TBD'}</span></div>
<div class="meta-row"><span>Type</span><span>${event.type || 'Event'}</span></div>
${event.location ? '<div class="meta-row"><span>Location</span><span>' + event.location + '</span></div>' : ''}
</div>
${event.description ? '<p style="font-size:13px;color:#444;line-height:1.5;margin-bottom:14px">' + event.description + '</p>' : ''}
${mediaHTML ? '<h3 style="font-size:15px;color:#0e4e96;margin-bottom:8px">Gallery</h3><div class="gallery">' + mediaHTML + '</div>' : ''}
<div class="ft">Event from ${companyName} · Generated ${new Date().toLocaleDateString('en-GB')} · JOBENG</div>
</body></html>`;
  };

  const generateEventPDF = async (event) => {
    setPdfLoading(true);
    try {
      const html = buildEventHTML(event);
      try {
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        const available = await Sharing.isAvailableAsync();
        if (available) { await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: event.title || 'Event', UTI: 'com.adobe.pdf' }); return; }
      } catch (fileErr) { console.warn('printToFileAsync unavailable, using print dialog:', fileErr?.message); }
      await Print.printAsync({ html });
    } catch (e) {
      Alert.alert('Error', 'Failed to generate event PDF.');
    } finally { setPdfLoading(false); }
  };

  const printEvent = async (event) => {
    try { await Print.printAsync({ html: buildEventHTML(event) }); }
    catch (e) { Alert.alert('Error', 'Print failed.'); }
  };



  // ---------- Document File Picker ----------
  const pickDocumentFile = async () => {
    try {
      setDocFileLoading(true);
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*', 'application/msword',
               'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
               'application/vnd.ms-excel',
               'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
               'text/plain', 'text/csv', '*/*'],
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0];
      let fileData = null;

      // For images and small files (< 3MB), convert to base64
      if (asset.size && asset.size < 3 * 1024 * 1024) {
        try {
          const base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
          const mime = asset.mimeType || 'application/octet-stream';
          fileData = `data:${mime};base64,${base64}`;
        } catch (e) {
          console.warn('Base64 conversion failed, keeping URI:', e?.message);
        }
      }

      const fileName = asset.name || asset.uri.split('/').pop() || 'file';

      // Auto-detect document type from mime
      let autoType = newDocument.type;
      if (asset.mimeType?.includes('pdf')) autoType = 'PDF';
      else if (asset.mimeType?.includes('image')) autoType = 'Image';
      else if (asset.mimeType?.includes('word') || asset.mimeType?.includes('document')) autoType = 'Contract';
      else if (asset.mimeType?.includes('sheet') || asset.mimeType?.includes('excel') || asset.mimeType?.includes('csv')) autoType = 'Spreadsheet';

      setNewDocument(prev => ({
        ...prev,
        file_url: asset.uri,
        file_name: fileName,
        file_size: asset.size || 0,
        mime_type: asset.mimeType || '',
        file_data: fileData,
        type: prev.type === 'Policy' ? autoType : prev.type,
        title: prev.title || fileName.replace(/\.[^.]+$/, ''),
      }));
    } catch (e) {
      console.error('Document picker error:', e);
      Alert.alert('Error', 'Failed to pick document');
    } finally {
      setDocFileLoading(false);
    }
  };

  const pickDocumentImage = async () => {
    try {
      setDocFileLoading(true);
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Please grant gallery permission');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        allowsEditing: false,
        quality: 0.8,
      });
      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        let fileData = null;
        try {
          const base64 = await FileSystem.readAsStringAsync(asset.uri, { encoding: 'base64' });
          const ext = asset.uri.split('.').pop()?.toLowerCase() || 'jpg';
          const mime = ext === 'png' ? 'image/png' : 'image/jpeg';
          fileData = `data:${mime};base64,${base64}`;
        } catch {}

        const fileName = asset.uri.split('/').pop() || 'photo.jpg';
        setNewDocument(prev => ({
          ...prev,
          file_url: asset.uri,
          file_name: fileName,
          file_size: asset.fileSize || 0,
          mime_type: asset.type === 'image' ? 'image/jpeg' : 'image/png',
          file_data: fileData,
          type: prev.type === 'Policy' ? 'Image' : prev.type,
          title: prev.title || fileName.replace(/\.[^.]+$/, ''),
        }));
      }
    } catch (e) {
      Alert.alert('Error', 'Failed to pick image');
    } finally { setDocFileLoading(false); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocIcon = (doc) => {
    const mime = doc?.mime_type || '';
    const type = doc?.type || '';
    if (mime.includes('pdf') || type === 'PDF') return 'file-pdf-box';
    if (mime.includes('image') || type === 'Image') return 'file-image';
    if (mime.includes('word') || type === 'Contract') return 'file-word';
    if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv') || type === 'Spreadsheet') return 'file-excel';
    if (type === 'Certificate') return 'certificate';
    if (type === 'Policy') return 'shield-check';
    return 'file-document';
  };

  const getDocIconColor = (doc) => {
    const mime = doc?.mime_type || '';
    const type = doc?.type || '';
    if (mime.includes('pdf') || type === 'PDF') return '#EF4444';
    if (mime.includes('image') || type === 'Image') return '#8B5CF6';
    if (mime.includes('word') || type === 'Contract') return '#2563EB';
    if (mime.includes('sheet') || mime.includes('excel') || type === 'Spreadsheet') return '#10B981';
    if (type === 'Certificate') return '#F59E0B';
    return '#0e4e96';
  };

  // ---------- Document Actions ----------
  const addDocument = async () => {
    if (!newDocument.title) {
      Alert.alert('Error', 'Document title is required');
      return;
    }
    const bizId = companyId || await getBusinessId();
    const docData = {
      title: newDocument.title,
      type: newDocument.type,
      description: newDocument.description,
      file_url: newDocument.file_data || newDocument.file_url || '',
      file_name: newDocument.file_name || '',
      file_size: newDocument.file_size || 0,
      mime_type: newDocument.mime_type || '',
      id: `doc_${Date.now()}`,
      company_id: bizId ? parseInt(bizId) : null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const updated = [...documents, docData];
    setDocuments(updated);
    await AsyncStorage.setItem(`company_documents_${bizId || 'default'}`, JSON.stringify(updated));
    // Sync to API
    if (bizId) {
      api.post('/api/documents', docData).catch((e) => console.warn('Document API create failed:', e?.message));
    }
    setShowDocumentModal(false);
    setNewDocument({ title: '', type: 'Policy', description: '', file_url: '', file_name: '', file_size: 0, mime_type: '', file_data: null });
    Alert.alert('Success', 'Document added');
  };

  const removeDocument = async (docId) => {
    Alert.alert('Remove Document', 'Are you sure?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          const updated = documents.filter(d => d.id !== docId);
          setDocuments(updated);
          const bizId = companyId || await getBusinessId();
          await AsyncStorage.setItem(`company_documents_${bizId || 'default'}`, JSON.stringify(updated));
          if (bizId) {
            api.delete(`/api/documents/${docId}`).catch((e) => console.warn('Document API delete failed:', e?.message));
          }
        },
      },
    ]);
  };



  // ==================== PDF / PRINT / PREVIEW ====================
  const buildProfileHTML = () => {
    const p = companyProfile || {};
    const stats = businessStats || {};
    const companyName = p.company_name || p.name || 'Company';
    const logoHTML = companyLogo
      ? `<img src="${companyLogo}" style="width:70px;height:70px;border-radius:35px;object-fit:cover;" />`
      : `<div style="width:70px;height:70px;border-radius:35px;background:#0e4e96;display:flex;align-items:center;justify-content:center;color:#fff;font-size:28px;font-weight:bold;">${companyName[0]}</div>`;

    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,'Segoe UI',Roboto,sans-serif;color:#222;padding:28px;background:#fff}
.hdr{display:flex;align-items:center;gap:18px;margin-bottom:24px;border-bottom:2px solid #0e4e96;padding-bottom:18px}
.hdr h1{font-size:24px;color:#0e4e96}.hdr p{font-size:13px;color:#666}
.sec{margin-bottom:20px}.sec h2{font-size:16px;color:#0e4e96;margin-bottom:10px;border-bottom:1px solid #e8e8e8;padding-bottom:5px}
.grid{display:flex;flex-wrap:wrap;gap:10px}.stat{flex:1;min-width:120px;background:#f8f9fa;border-radius:8px;padding:14px;text-align:center}
.stat .v{font-size:20px;font-weight:bold;color:#0e4e96}.stat .l{font-size:11px;color:#888;margin-top:3px}
table{width:100%;border-collapse:collapse}td{padding:8px 10px;border-bottom:1px solid #f0f0f0;font-size:13px}td:first-child{font-weight:600;color:#555;width:38%}
.chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:6px}.chip{background:#e8f0fb;color:#0e4e96;padding:3px 10px;border-radius:14px;font-size:11px;font-weight:500}
.ft{margin-top:28px;text-align:center;font-size:10px;color:#aaa;border-top:1px solid #eee;padding-top:10px}
</style></head><body>
<div class="hdr">${logoHTML}<div><h1>${companyName}</h1><p>${p.industry || ''}${p.headquarters ? ' · ' + p.headquarters : ''}</p>${p.website ? '<p>' + p.website + '</p>' : ''}</div></div>
${p.description ? '<div class="sec"><h2>About</h2><p style="font-size:13px;color:#444;line-height:1.5">' + p.description + '</p></div>' : ''}
<div class="sec"><h2>Key Statistics</h2><div class="grid">
<div class="stat"><div class="v">${safeGetNumber(stats.employeeCount || p.employee_count || p.employees, 0)}</div><div class="l">Employees</div></div>
<div class="stat"><div class="v">${safeGetNumber(stats.activeProjects, 0)}</div><div class="l">Projects</div></div>
<div class="stat"><div class="v">${safeGetNumber(stats.totalClients, 0)}</div><div class="l">Clients</div></div>
<div class="stat"><div class="v">${formatCurrency(stats.totalRevenue)}</div><div class="l">Revenue</div></div>
</div></div>
<div class="sec"><h2>Contact</h2><table>
${p.email ? '<tr><td>Email</td><td>' + p.email + '</td></tr>' : ''}
${p.phone ? '<tr><td>Phone</td><td>' + p.phone + '</td></tr>' : ''}
${p.website ? '<tr><td>Website</td><td>' + p.website + '</td></tr>' : ''}
${p.headquarters ? '<tr><td>Headquarters</td><td>' + p.headquarters + '</td></tr>' : ''}
</table></div>
${(p.vat_number || p.registration_number) ? '<div class="sec"><h2>Legal</h2><table>' + (p.vat_number ? '<tr><td>VAT</td><td>' + p.vat_number + '</td></tr>' : '') + (p.registration_number ? '<tr><td>Reg No.</td><td>' + p.registration_number + '</td></tr>' : '') + (p.legal_name ? '<tr><td>Legal Name</td><td>' + p.legal_name + '</td></tr>' : '') + '</table></div>' : ''}
${p.bank_name ? '<div class="sec"><h2>Banking</h2><table><tr><td>Bank</td><td>' + p.bank_name + '</td></tr>' + (p.bank_sort_code ? '<tr><td>Sort Code</td><td>' + p.bank_sort_code + '</td></tr>' : '') + (p.bank_account ? '<tr><td>Account</td><td>' + p.bank_account + '</td></tr>' : '') + '</table></div>' : ''}
${(p.specializations || []).length > 0 ? '<div class="sec"><h2>Specializations</h2><div class="chips">' + p.specializations.map(function(s) { return '<span class="chip">' + s + '</span>'; }).join('') + '</div></div>' : ''}
${teamMembers.length > 0 ? '<div class="sec"><h2>Team (' + teamMembers.length + ')</h2><table>' + teamMembers.slice(0, 15).map(function(m) { return '<tr><td>' + (m.name || m.full_name || '-') + '</td><td>' + (m.position || m.role || '') + ' · ' + (m.email || '') + '</td></tr>'; }).join('') + '</table></div>' : ''}
${documents.length > 0 ? '<div class="sec"><h2>Documents (' + documents.length + ')</h2><table>' + documents.map(function(d) { return '<tr><td>' + d.title + '</td><td>' + (d.type || 'Document') + ' — ' + (d.description || '') + '</td></tr>'; }).join('') + '</table></div>' : ''}
<div class="ft">Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · ${companyName} · Powered by JOBENG</div>
</body></html>`;
  };

  const buildDocumentHTML = (doc) => {
    const companyName = companyProfile?.company_name || companyProfile?.name || 'Company';
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>
*{box-sizing:border-box;margin:0;padding:0}body{font-family:-apple-system,'Segoe UI',Roboto,sans-serif;color:#222;padding:32px;background:#fff}
.hdr{display:flex;align-items:center;gap:16px;margin-bottom:24px;border-bottom:2px solid #0e4e96;padding-bottom:16px}
.hdr h1{font-size:22px;color:#0e4e96}.hdr p{font-size:13px;color:#666}
.meta{background:#f8f9fa;border-radius:8px;padding:16px;margin-bottom:20px}
.meta-row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #eee;font-size:13px}
.meta-row span:first-child{color:#888}.meta-row span:last-child{color:#0e4e96;font-weight:600}
.body-text{font-size:14px;color:#333;line-height:1.6;margin-top:16px;white-space:pre-wrap}
.ft{margin-top:32px;text-align:center;font-size:10px;color:#aaa;border-top:1px solid #eee;padding-top:10px}
</style></head><body>
<div class="hdr">${companyLogo ? '<img src="' + companyLogo + '" style="width:50px;height:50px;border-radius:25px;object-fit:cover;" />' : ''}<div><h1>${doc.title || 'Document'}</h1><p>${companyName}</p></div></div>
<div class="meta">
<div class="meta-row"><span>Type</span><span>${doc.type || 'Document'}</span></div>
<div class="meta-row"><span>Date</span><span>${doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB') : 'N/A'}</span></div>
${doc.description ? '<div class="meta-row"><span>Description</span><span>' + doc.description + '</span></div>' : ''}
${doc.file_url ? '<div class="meta-row"><span>File</span><span>' + doc.file_url + '</span></div>' : ''}
</div>
${doc.content ? '<div class="body-text">' + doc.content + '</div>' : '<p style="color:#888;text-align:center;padding:40px 0;">Document content will be displayed here when available.</p>'}
<div class="ft">Document from ${companyName} · Generated ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · JOBENG</div>
</body></html>`;
  };

  const generatePDF = async () => {
    setPdfLoading(true);
    try {
      const html = buildProfileHTML();
      try {
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        const available = await Sharing.isAvailableAsync();
        if (available) { await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: 'Company Profile PDF', UTI: 'com.adobe.pdf' }); return; }
      } catch (fileErr) { console.warn('printToFileAsync unavailable, using print dialog:', fileErr?.message); }
      await Print.printAsync({ html });
    } catch (e) {
      console.error('PDF error:', e);
      Alert.alert('Error', 'Failed to generate PDF.');
    } finally { setPdfLoading(false); }
  };

  const generateDocumentPDF = async (doc) => {
    setPdfLoading(true);
    try {
      const html = buildDocumentHTML(doc);
      try {
        const { uri } = await Print.printToFileAsync({ html, base64: false });
        const available = await Sharing.isAvailableAsync();
        if (available) { await Sharing.shareAsync(uri, { mimeType: 'application/pdf', dialogTitle: doc.title || 'Document', UTI: 'com.adobe.pdf' }); return; }
      } catch (fileErr) { console.warn('printToFileAsync unavailable, using print dialog:', fileErr?.message); }
      await Print.printAsync({ html });
    } catch (e) {
      console.error('Doc PDF error:', e);
      Alert.alert('Error', 'Failed to generate document PDF.');
    } finally { setPdfLoading(false); }
  };

  const printProfile = async () => {
    try { await Print.printAsync({ html: buildProfileHTML() }); }
    catch (e) { Alert.alert('Error', 'Print failed.'); }
  };

  const printDocument = async (doc) => {
    try { await Print.printAsync({ html: buildDocumentHTML(doc) }); }
    catch (e) { Alert.alert('Error', 'Print failed.'); }
  };

  const shareProfileText = async () => {
    const p = companyProfile || {};
    const lines = [
      p.company_name || p.name || 'Company Profile',
      p.industry ? `Industry: ${p.industry}` : '',
      p.email ? `Email: ${p.email}` : '',
      p.phone ? `Phone: ${p.phone}` : '',
      p.website ? `Website: ${p.website}` : '',
      p.headquarters ? `HQ: ${p.headquarters}` : '',
      p.vat_number ? `VAT: ${p.vat_number}` : '',
    ].filter(Boolean).join('\n');
    try { await Share.share({ title: `${p.company_name || p.name || 'Company'} Profile`, message: lines }); } catch {}
  };

  const showExportOptions = () => {
    Alert.alert('Export Company Profile', 'Choose an option', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Preview', onPress: () => setShowPreviewModal(true) },
      { text: 'Export PDF', onPress: generatePDF },
      { text: 'Print', onPress: printProfile },
      { text: 'Share Text', onPress: shareProfileText },
    ]);
  };

  const showDocumentActions = (doc) => {
    Alert.alert(doc.title || 'Document', 'Choose an action', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Preview', onPress: () => { setPreviewDocument(doc); setShowDocPreviewModal(true); } },
      { text: 'Export PDF', onPress: () => generateDocumentPDF(doc) },
      { text: 'Print', onPress: () => printDocument(doc) },
      { text: 'Delete', style: 'destructive', onPress: () => removeDocument(doc.id) },
    ]);
  };

  // ---------- Image Picker ----------
  // Logo is loaded from company profile — no upload on this screen

  // ---------- Save Edited Company ----------
  const handleSaveCompany = async () => {
    if (!editData.name || !editData.industry || !editData.email) {
      Alert.alert('Validation Error', 'Name, industry, and email are required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editData.email)) {
      Alert.alert('Invalid Email', 'Please enter a valid email');
      return;
    }
    await saveCompanyProfile(editData);
    setShowEditModal(false);
    Alert.alert('Success', 'Company information updated');
  };

  // ---------- Refresh ----------
  const onRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  useEffect(() => {
    loadAllData();
  }, []);

  // ---------- Safe Helpers ----------
  const safeGetString = (val, fallback = '') => (val && typeof val === 'string' ? val : fallback);
  const safeGetNumber = (val, fallback = 0) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      const parsed = parseFloat(val);
      return isNaN(parsed) ? fallback : parsed;
    }
    return fallback;
  };
  const safeGetArray = (val, fallback = []) => (Array.isArray(val) ? val : fallback);

  // ---------- Formatting ----------
  const formatCurrency = (amount) => {
    const num = safeGetNumber(amount, 0);
    if (num >= 1e6) return `£${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `£${(num / 1e3).toFixed(0)}K`;
    return `£${num.toLocaleString()}`;
  };

  const getUserInitials = (name) => {
    const str = safeGetString(name, 'U');
    return str.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // ---------- Render UI ----------
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={48} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading company info...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      <LinearGradient colors={['#0e4e96', '#2670ba']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={styles.headerTitleContainer}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
              ) : null}
              <View>
                <Text style={styles.headerTitle}>Company Information</Text>
                <Text style={styles.headerSubtitle}>{safeGetString(companyProfile?.company_name || companyProfile?.name, 'Business Portal')}</Text>
              </View>
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <TouchableOpacity style={styles.editButton} onPress={showExportOptions}>
              <Ionicons name="download-outline" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => navigation.navigate('CompanyAnalytics', { companyProfile, companyId, subscription: routeSubscription })}
            >
              <MaterialCommunityIcons name="chart-line" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={styles.editButton} onPress={() => setShowEditModal(true)}>
              <MaterialCommunityIcons name="pencil" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      <ScrollView
        style={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        showsVerticalScrollIndicator={false}
      >
        {/* Company Header */}
        <View style={styles.companyHeaderSection}>
          <View style={styles.logoContainer}>
            <View style={styles.logoWrapper}>
              {companyProfile?.logo ? (
                <Image source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} style={styles.companyLogo} />
              ) : (
                <View style={styles.logoPlaceholder}>
                  <MaterialCommunityIcons name="office-building" size={40} color="#0e4e96" />
                </View>
              )}
            </View>
          </View>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{safeGetString(companyProfile?.company_name || companyProfile?.name)}</Text>
            <Text style={styles.companyIndustry}>{safeGetString(companyProfile?.industry)}</Text>
            <Text style={styles.companyDescription} numberOfLines={3}>
              {safeGetString(companyProfile?.description, 'Company description')}
            </Text>
            <View style={styles.companyStats}>
              <View style={styles.companyStat}>
                <Text style={styles.companyStatValue}>{safeGetNumber(companyProfile?.employee_count || companyProfile?.employees, 0)}</Text>
                <Text style={styles.companyStatLabel}>Employees</Text>
              </View>
              <View style={styles.companyStat}>
                <Text style={styles.companyStatValue}>{safeGetNumber(companyProfile?.founded || companyProfile?.established, new Date().getFullYear())}</Text>
                <Text style={styles.companyStatLabel}>Founded</Text>
              </View>
              <View style={styles.companyStat}>
                <Text style={styles.companyStatValue}>{safeGetNumber(businessStats?.totalProjects, 0)}</Text>
                <Text style={styles.companyStatLabel}>Projects</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Tabs */}
        <View style={styles.tabNavigation}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 4 }}>
          {[
            { id: 'overview', title: 'Overview', icon: 'information' },
            { id: 'team', title: 'Team', icon: 'account-group' },
            { id: 'locations', title: 'Locations', icon: 'map-marker' },
            { id: 'awards', title: 'Awards', icon: 'trophy' },
            { id: 'documents', title: 'Docs', icon: 'file-document-multiple' },
            { id: 'events', title: 'Events', icon: 'calendar-star' },
          ].map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[styles.tabButton, activeTab === tab.id && styles.tabButtonActive]}
              onPress={() => setActiveTab(tab.id)}
            >
              <MaterialCommunityIcons name={tab.icon} size={20} color={activeTab === tab.id ? '#0e4e96' : '#64748B'} />
              <Text style={[styles.tabButtonText, activeTab === tab.id && styles.tabButtonTextActive]}>{tab.title}</Text>
            </TouchableOpacity>
          ))}
          </ScrollView>
        </View>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <View style={styles.tabContent}>
            {/* Statistics */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Business Statistics</Text>
              <View style={styles.statsGrid}>
                <View style={styles.statCard}>
                  <MaterialCommunityIcons name="cash" size={24} color="#10B981" />
                  <Text style={styles.statValue}>{formatCurrency(businessStats?.totalRevenue)}</Text>
                  <Text style={styles.statLabel}>Total Revenue</Text>
                </View>
                <View style={styles.statCard}>
                  <MaterialCommunityIcons name="account-group" size={24} color="#3B82F6" />
                  <Text style={styles.statValue}>{safeGetNumber(businessStats?.totalClients)}</Text>
                  <Text style={styles.statLabel}>Total Clients</Text>
                </View>
                <View style={styles.statCard}>
                  <MaterialCommunityIcons name="folder-multiple" size={24} color="#8B5CF6" />
                  <Text style={styles.statValue}>{safeGetNumber(businessStats?.activeProjects)}</Text>
                  <Text style={styles.statLabel}>Active Projects</Text>
                </View>
                <View style={styles.statCard}>
                  <MaterialCommunityIcons name="star" size={24} color="#EC4899" />
                  <Text style={styles.statValue}>{safeGetNumber(businessStats?.clientSatisfaction)}</Text>
                  <Text style={styles.statLabel}>Satisfaction</Text>
                </View>
              </View>

              {/* Navigate to Analytics */}
              <TouchableOpacity
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#0e4e96',
                  paddingVertical: 14,
                  borderRadius: 12,
                  marginTop: 16,
                  gap: 8,
                }}
                onPress={() => navigation.navigate('CompanyAnalytics', {
                  companyProfile,
                  companyId,
                  subscription: routeSubscription,
                })}
              >
                <MaterialCommunityIcons name="chart-line" size={20} color="#fff" />
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#fff' }}>View Full Analytics & BI</Text>
                <Ionicons name="arrow-forward" size={18} color="#fff" />
              </TouchableOpacity>
            </View>

            {/* Company Details */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Company Details</Text>
              <View style={styles.detailsList}>
                <View style={styles.detailItem}>
                  <MaterialCommunityIcons name="web" size={20} color="#0e4e96" />
                  <View style={styles.detailContent}>
                    <Text style={styles.detailLabel}>Website</Text>
                    <Text style={styles.detailValue}>{safeGetString(companyProfile?.website, 'Not provided')}</Text>
                  </View>
                </View>
                <View style={styles.detailItem}>
                  <MaterialCommunityIcons name="email" size={20} color="#0e4e96" />
                  <View style={styles.detailContent}>
                    <Text style={styles.detailLabel}>Email</Text>
                    <Text style={styles.detailValue}>{safeGetString(companyProfile?.email, 'Not provided')}</Text>
                  </View>
                </View>
                <View style={styles.detailItem}>
                  <MaterialCommunityIcons name="phone" size={20} color="#0e4e96" />
                  <View style={styles.detailContent}>
                    <Text style={styles.detailLabel}>Phone</Text>
                    <Text style={styles.detailValue}>{safeGetString(companyProfile?.phone, 'Not provided')}</Text>
                  </View>
                </View>
                <View style={styles.detailItem}>
                  <MaterialCommunityIcons name="map-marker" size={20} color="#0e4e96" />
                  <View style={styles.detailContent}>
                    <Text style={styles.detailLabel}>Headquarters</Text>
                    <Text style={styles.detailValue}>{safeGetString(companyProfile?.headquarters, 'Not provided')}</Text>
                  </View>
                </View>
                {companyProfile?.vat_number ? (
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="file-certificate" size={20} color="#0e4e96" />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>VAT Number</Text>
                      <Text style={styles.detailValue}>{companyProfile.vat_number}</Text>
                    </View>
                  </View>
                ) : null}
                {companyProfile?.registration_number ? (
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="identifier" size={20} color="#0e4e96" />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>Registration No.</Text>
                      <Text style={styles.detailValue}>{companyProfile.registration_number}</Text>
                    </View>
                  </View>
                ) : null}
                {companyProfile?.bank_name ? (
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="bank" size={20} color="#0e4e96" />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>Bank</Text>
                      <Text style={styles.detailValue}>{companyProfile.bank_name} — {companyProfile.bank_sort_code || ''}</Text>
                    </View>
                  </View>
                ) : null}
                {companyProfile?.legal_name ? (
                  <View style={styles.detailItem}>
                    <MaterialCommunityIcons name="gavel" size={20} color="#0e4e96" />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>Legal Name</Text>
                      <Text style={styles.detailValue}>{companyProfile.legal_name}</Text>
                    </View>
                  </View>
                ) : null}
              </View>
            </View>

            {/* Specializations */}
            {safeGetArray(companyProfile?.specializations).length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Specializations</Text>
                <View style={styles.specializationsGrid}>
                  {companyProfile.specializations.map((spec, i) => (
                    <View key={i} style={styles.specializationChip}>
                      <Text style={styles.specializationText}>{spec}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Certifications */}
            {safeGetArray(companyProfile?.certifications).length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Certifications</Text>
                <View style={styles.certificationsList}>
                  {companyProfile.certifications.map((cert, i) => (
                    <View key={i} style={styles.certificationItem}>
                      <MaterialCommunityIcons name="certificate" size={20} color="#10B981" />
                      <Text style={styles.certificationText}>{cert}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}
          </View>
        )}

        {activeTab === 'team' && (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Team Members</Text>
                <TouchableOpacity style={styles.addButton} onPress={() => setShowTeamModal(true)}>
                  <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              <View style={styles.teamGrid}>
                {teamMembers.map((member) => (
                  <View key={member.id} style={styles.teamCard}>
                    <TouchableOpacity style={styles.teamRemove} onPress={() => removeTeamMember(member.id)}>
                      <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                    </TouchableOpacity>
                    <View style={styles.teamMemberHeader}>
                      <View style={styles.teamAvatarPlaceholder}>
                        <Text style={styles.teamAvatarInitials}>{getUserInitials(member.name)}</Text>
                      </View>
                      <View style={[styles.statusIndicator, { backgroundColor: member.status === 'online' ? '#10B981' : '#94A3B8' }]} />
                    </View>
                    <Text style={styles.teamMemberName}>{member.name}</Text>
                    <Text style={styles.teamMemberPosition}>{member.position}</Text>
                    <Text style={styles.teamMemberDepartment}>{member.department}</Text>
                    <View style={styles.teamMemberActions}>
                      <TouchableOpacity style={styles.teamActionButton}>
                        <MaterialCommunityIcons name="email" size={16} color="#0e4e96" />
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.teamActionButton}>
                        <MaterialCommunityIcons name="phone" size={16} color="#0e4e96" />
                      </TouchableOpacity>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {activeTab === 'locations' && (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Office Locations</Text>
                <TouchableOpacity style={styles.addButton} onPress={() => setShowLocationModal(true)}>
                  <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              <View style={styles.locationsList}>
                {locations.map((loc, idx) => (
                  <View key={idx} style={styles.locationCard}>
                    <TouchableOpacity style={styles.locationRemove} onPress={() => removeLocation(idx)}>
                      <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                    </TouchableOpacity>
                    <View style={styles.locationHeader}>
                      <View style={styles.locationIcon}>
                        <MaterialCommunityIcons name={loc.type === 'Headquarters' ? 'office-building' : 'office-building-outline'} size={24} color="#0e4e96" />
                      </View>
                      <View style={styles.locationInfo}>
                        <Text style={styles.locationName}>{loc.name}</Text>
                        <Text style={styles.locationType}>{loc.type}</Text>
                      </View>
                      <Text style={styles.locationEmployees}>{safeGetNumber(loc.employees)} emp</Text>
                    </View>
                    <Text style={styles.locationAddress}>{loc.address}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}

        {activeTab === 'awards' && (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Awards & Recognition</Text>
                <TouchableOpacity style={styles.addButton} onPress={() => setShowAwardModal(true)}>
                  <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              <View style={styles.awardsList}>
                {awards.map((award, idx) => (
                  <View key={idx} style={styles.awardCard}>
                    <TouchableOpacity style={styles.awardRemove} onPress={() => removeAward(idx)}>
                      <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                    </TouchableOpacity>
                    <View style={styles.awardIcon}>
                      <MaterialCommunityIcons name="trophy" size={24} color="#F59E0B" />
                    </View>
                    <View style={styles.awardInfo}>
                      <Text style={styles.awardTitle}>{award.title}</Text>
                      <Text style={styles.awardOrganization}>{award.organization}</Text>
                      <Text style={styles.awardYear}>{award.year}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </View>
        )}


        {activeTab === 'events' && (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Company Events</Text>
                <TouchableOpacity style={styles.addButton} onPress={() => setShowEventModal(true)}>
                  <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              {events.length > 0 ? (
                <View style={{ gap: 12 }}>
                  {events.map((event) => {
                    const media = Array.isArray(event.media) ? event.media : (typeof event.media === 'string' ? (function() { try { return JSON.parse(event.media); } catch { return []; } })() : []);
                    return (
                      <TouchableOpacity
                        key={event.id || `evt-${event.title}`}
                        style={[styles.locationCard, { padding: 0, overflow: 'hidden' }]}
                        onPress={() => showEventActions(event)}
                        activeOpacity={0.7}
                      >
                        {/* Media preview strip */}
                        {media.length > 0 && (
                          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ height: 120 }} contentContainerStyle={{ gap: 0 }}>
                            {media.slice(0, 5).map((m, mi) => {
                              const src = m.base64 || m.uri;
                              return m.type === 'video' ? (
                                <View key={mi} style={{ width: 160, height: 120, backgroundColor: '#000', alignItems: 'center', justifyContent: 'center' }}>
                                  <Ionicons name="play-circle" size={36} color="#fff" />
                                  <Text style={{ color: '#fff', fontSize: 10, marginTop: 4 }}>Video</Text>
                                </View>
                              ) : (
                                <Image key={mi} source={{ uri: src }} style={{ width: 160, height: 120 }} resizeMode="cover" />
                              );
                            })}
                            {media.length > 5 && (
                              <View style={{ width: 80, height: 120, backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center' }}>
                                <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>+{media.length - 5}</Text>
                              </View>
                            )}
                          </ScrollView>
                        )}
                        <View style={{ padding: 16 }}>
                          <TouchableOpacity style={styles.locationRemove} onPress={() => removeEvent(event.id)}>
                            <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                          </TouchableOpacity>
                          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                            <MaterialCommunityIcons name={event.type === 'Conference' ? 'account-group' : event.type === 'Workshop' ? 'school' : event.type === 'Social' ? 'glass-cocktail' : 'calendar'} size={20} color="#0e4e96" style={{ marginRight: 8 }} />
                            <Text style={[styles.locationName, { flex: 1 }]}>{event.title}</Text>
                          </View>
                          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 4 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                              <Ionicons name="calendar" size={14} color="#64748B" />
                              <Text style={{ fontSize: 12, color: '#64748B' }}>{event.date || 'TBD'}</Text>
                            </View>
                            {event.location ? (
                              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                <Ionicons name="location" size={14} color="#64748B" />
                                <Text style={{ fontSize: 12, color: '#64748B' }}>{event.location}</Text>
                              </View>
                            ) : null}
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                              <Ionicons name="images" size={14} color="#64748B" />
                              <Text style={{ fontSize: 12, color: '#64748B' }}>{media.length} media</Text>
                            </View>
                          </View>
                          {event.description ? <Text style={{ fontSize: 13, color: '#64748B', marginTop: 4 }} numberOfLines={2}>{event.description}</Text> : null}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              ) : (
                <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                  <MaterialCommunityIcons name="calendar-blank" size={48} color="#CBD5E1" />
                  <Text style={{ fontSize: 16, color: '#64748B', marginTop: 12, marginBottom: 16 }}>No events yet</Text>
                  <TouchableOpacity style={[styles.modalButton, { paddingHorizontal: 24 }]} onPress={() => setShowEventModal(true)}>
                    <Text style={styles.modalButtonText}>Add First Event</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        )}

        {activeTab === 'documents' && (
          <View style={styles.tabContent}>
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>Company Documents</Text>
                <TouchableOpacity style={styles.addButton} onPress={() => setShowDocumentModal(true)}>
                  <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                </TouchableOpacity>
              </View>
              {documents.length > 0 ? (
                <View style={styles.awardsList}>
                  {documents.map((doc) => (
                    <TouchableOpacity
                      key={doc.id || `doc-${doc.title}`}
                      style={styles.awardCard}
                      onPress={() => showDocumentActions(doc)}
                      activeOpacity={0.7}
                    >
                      <TouchableOpacity style={styles.awardRemove} onPress={() => removeDocument(doc.id)}>
                        <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                      </TouchableOpacity>
                      <View style={[styles.awardIcon, { backgroundColor: getDocIconColor(doc) + '15' }]}>
                        <MaterialCommunityIcons name={getDocIcon(doc)} size={24} color={getDocIconColor(doc)} />
                      </View>
                      <View style={styles.awardInfo}>
                        <Text style={styles.awardTitle}>{doc.title}</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          <Text style={[styles.awardOrganization, { color: getDocIconColor(doc) }]}>{doc.type || 'Document'}</Text>
                          {doc.file_name ? <Text style={{ fontSize: 11, color: '#94A3B8' }}>· {doc.file_name}</Text> : null}
                        </View>
                        {doc.file_size ? <Text style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>{formatFileSize(doc.file_size)}{doc.mime_type ? ' · ' + doc.mime_type.split('/').pop().toUpperCase() : ''}</Text> : null}
                        {doc.description ? <Text style={styles.awardYear}>{doc.description}</Text> : null}
                        {doc.created_at ? <Text style={[styles.awardYear, { color: '#94A3B8' }]}>{new Date(doc.created_at).toLocaleDateString()}</Text> : null}
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View style={{ alignItems: 'center', paddingVertical: 40 }}>
                  <MaterialCommunityIcons name="file-document-outline" size={48} color="#CBD5E1" />
                  <Text style={{ fontSize: 16, color: '#64748B', marginTop: 12 }}>No documents yet</Text>
                  <TouchableOpacity
                    style={[styles.modalButton, { marginTop: 16, paddingHorizontal: 24 }]}
                    onPress={() => setShowDocumentModal(true)}
                  >
                    <Text style={styles.modalButtonText}>Add First Document</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          </View>
        )}
      </ScrollView>

      {/* Add Document Modal */}
      <Modal visible={showDocumentModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.editModalContainer, { height: height * 0.8 }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Upload Document</Text>
              <TouchableOpacity onPress={() => { setShowDocumentModal(false); setNewDocument({ title: '', type: 'Policy', description: '', file_url: '', file_name: '', file_size: 0, mime_type: '', file_data: null }); }}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ flex: 1 }}>
              <View style={styles.modalBody}>
                {/* File Picker Section */}
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0F172A', marginBottom: 12 }}>Attach File</Text>

                {newDocument.file_name ? (
                  <View style={{ backgroundColor: '#F0F8FF', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#BFDBFE' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                      <View style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: '#fff', alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: '#E2E8F0' }}>
                        <MaterialCommunityIcons name={getDocIcon(newDocument)} size={24} color={getDocIconColor(newDocument)} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 14, fontWeight: '600', color: '#0F172A' }} numberOfLines={1}>{newDocument.file_name}</Text>
                        <Text style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                          {newDocument.mime_type?.split('/').pop()?.toUpperCase() || 'File'}{newDocument.file_size ? ` · ${formatFileSize(newDocument.file_size)}` : ''}
                        </Text>
                      </View>
                      <TouchableOpacity onPress={() => setNewDocument(prev => ({ ...prev, file_url: '', file_name: '', file_size: 0, mime_type: '', file_data: null }))}>
                        <Ionicons name="close-circle" size={22} color="#EF4444" />
                      </TouchableOpacity>
                    </View>
                    {/* Image preview */}
                    {newDocument.mime_type?.includes('image') && (newDocument.file_data || newDocument.file_url) ? (
                      <Image source={{ uri: newDocument.file_data || newDocument.file_url }} style={{ width: '100%', height: 160, borderRadius: 8, marginTop: 10 }} resizeMode="cover" />
                    ) : null}
                  </View>
                ) : (
                  <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                    <TouchableOpacity
                      disabled={docFileLoading}
                      style={{ flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', borderRadius: 12, paddingVertical: 20, gap: 8, borderWidth: 1.5, borderColor: '#E2E8F0', borderStyle: 'dashed' }}
                      onPress={pickDocumentFile}
                    >
                      {docFileLoading ? <ActivityIndicator size={24} color="#0e4e96" /> : <Ionicons name="document-attach" size={28} color="#0e4e96" />}
                      <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 13 }}>Browse Files</Text>
                      <Text style={{ color: '#94A3B8', fontSize: 10 }}>PDF, Word, Excel, etc.</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      disabled={docFileLoading}
                      style={{ flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', borderRadius: 12, paddingVertical: 20, gap: 8, borderWidth: 1.5, borderColor: '#E2E8F0', borderStyle: 'dashed' }}
                      onPress={pickDocumentImage}
                    >
                      <Ionicons name="images" size={28} color="#8B5CF6" />
                      <Text style={{ color: '#8B5CF6', fontWeight: '600', fontSize: 13 }}>Photo / Scan</Text>
                      <Text style={{ color: '#94A3B8', fontSize: 10 }}>JPG, PNG images</Text>
                    </TouchableOpacity>
                  </View>
                )}

                {/* Document Details */}
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0F172A', marginBottom: 12 }}>Document Details</Text>
                <TextInput style={styles.modalInput} placeholder="Document Title *" value={newDocument.title} onChangeText={t => setNewDocument({...newDocument, title: t})} />
                <TextInput style={styles.modalInput} placeholder="Type (Policy/Contract/Certificate/Report/PDF/Image)" value={newDocument.type} onChangeText={t => setNewDocument({...newDocument, type: t})} />
                <TextInput style={[styles.modalInput, { height: 80, textAlignVertical: 'top' }]} placeholder="Description (optional)" value={newDocument.description} onChangeText={t => setNewDocument({...newDocument, description: t})} multiline />

                <TouchableOpacity style={[styles.modalButton, { marginTop: 8 }]} onPress={addDocument}>
                  <Text style={styles.modalButtonText}>Save Document</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Edit Company Modal */}
      <Modal visible={showEditModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.editModalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Company</Text>
              <TouchableOpacity onPress={() => setShowEditModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.editModalContent}>
              <View style={styles.editSection}>
                <Text style={styles.editSectionTitle}>Basic Information</Text>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Company Name *</Text>
                  <TextInput style={styles.textInput} value={editData.name} onChangeText={t => setEditData({...editData, name: t})} placeholder="Enter company name" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Industry *</Text>
                  <TextInput style={styles.textInput} value={editData.industry} onChangeText={t => setEditData({...editData, industry: t})} placeholder="Enter industry" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Description</Text>
                  <TextInput style={[styles.textInput, styles.textArea]} value={editData.description} onChangeText={t => setEditData({...editData, description: t})} placeholder="Enter description" multiline numberOfLines={3} />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Email *</Text>
                  <TextInput style={styles.textInput} value={editData.email} onChangeText={t => setEditData({...editData, email: t})} placeholder="Email" keyboardType="email-address" autoCapitalize="none" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Phone</Text>
                  <TextInput style={styles.textInput} value={editData.phone} onChangeText={t => setEditData({...editData, phone: t})} placeholder="Phone" keyboardType="phone-pad" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Website</Text>
                  <TextInput style={styles.textInput} value={editData.website} onChangeText={t => setEditData({...editData, website: t})} placeholder="Website" keyboardType="url" autoCapitalize="none" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Headquarters</Text>
                  <TextInput style={styles.textInput} value={editData.headquarters} onChangeText={t => setEditData({...editData, headquarters: t})} placeholder="Headquarters" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Founded Year</Text>
                  <TextInput style={styles.textInput} value={String(editData.established || '')} onChangeText={t => setEditData({...editData, established: t ? parseInt(t) : ''})} placeholder="Founded" keyboardType="numeric" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Employees Count</Text>
                  <TextInput style={styles.textInput} value={String(editData.employees || editData.employee_count || '')} onChangeText={t => setEditData({...editData, employees: t ? parseInt(t) : '', employee_count: t ? parseInt(t) : ''})} placeholder="Number of employees" keyboardType="numeric" />
                </View>
              </View>

              <View style={styles.editSection}>
                <Text style={styles.editSectionTitle}>Legal & Financial</Text>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Legal Name</Text>
                  <TextInput style={styles.textInput} value={editData.legal_name || ''} onChangeText={t => setEditData({...editData, legal_name: t})} placeholder="Registered legal name" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>VAT Number</Text>
                  <TextInput style={styles.textInput} value={editData.vat_number || ''} onChangeText={t => setEditData({...editData, vat_number: t})} placeholder="VAT registration number" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Registration Number</Text>
                  <TextInput style={styles.textInput} value={editData.registration_number || ''} onChangeText={t => setEditData({...editData, registration_number: t})} placeholder="Company registration number" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Bank Name</Text>
                  <TextInput style={styles.textInput} value={editData.bank_name || ''} onChangeText={t => setEditData({...editData, bank_name: t})} placeholder="Bank name" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Bank Account</Text>
                  <TextInput style={styles.textInput} value={editData.bank_account || ''} onChangeText={t => setEditData({...editData, bank_account: t})} placeholder="Account number" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Sort Code</Text>
                  <TextInput style={styles.textInput} value={editData.bank_sort_code || ''} onChangeText={t => setEditData({...editData, bank_sort_code: t})} placeholder="Sort code" />
                </View>
              </View>

              <View style={styles.editSection}>
                <Text style={styles.editSectionTitle}>Social Media</Text>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>LinkedIn</Text>
                  <TextInput style={styles.textInput} value={editData.socialMedia?.linkedin || ''} onChangeText={t => setEditData({...editData, socialMedia: {...(editData.socialMedia || {}), linkedin: t}})} placeholder="LinkedIn URL" autoCapitalize="none" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Facebook</Text>
                  <TextInput style={styles.textInput} value={editData.socialMedia?.facebook || ''} onChangeText={t => setEditData({...editData, socialMedia: {...(editData.socialMedia || {}), facebook: t}})} placeholder="Facebook URL" autoCapitalize="none" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Twitter / X</Text>
                  <TextInput style={styles.textInput} value={editData.socialMedia?.twitter || ''} onChangeText={t => setEditData({...editData, socialMedia: {...(editData.socialMedia || {}), twitter: t}})} placeholder="Twitter handle" autoCapitalize="none" />
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Instagram</Text>
                  <TextInput style={styles.textInput} value={editData.socialMedia?.instagram || ''} onChangeText={t => setEditData({...editData, socialMedia: {...(editData.socialMedia || {}), instagram: t}})} placeholder="Instagram handle" autoCapitalize="none" />
                </View>
              </View>

              <TouchableOpacity style={styles.saveButton} onPress={handleSaveCompany}>
                <Text style={styles.saveButtonText}>Save Changes</Text>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Add Team Member Modal */}
      <Modal visible={showTeamModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.smallModal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Team Member</Text>
              <TouchableOpacity onPress={() => setShowTeamModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              <TextInput style={styles.modalInput} placeholder="Full Name *" value={newTeamMember.name} onChangeText={t => setNewTeamMember({...newTeamMember, name: t})} />
              <TextInput style={styles.modalInput} placeholder="Position *" value={newTeamMember.position} onChangeText={t => setNewTeamMember({...newTeamMember, position: t})} />
              <TextInput style={styles.modalInput} placeholder="Department" value={newTeamMember.department} onChangeText={t => setNewTeamMember({...newTeamMember, department: t})} />
              <TextInput style={styles.modalInput} placeholder="Email *" value={newTeamMember.email} onChangeText={t => setNewTeamMember({...newTeamMember, email: t})} keyboardType="email-address" autoCapitalize="none" />
              <TextInput style={styles.modalInput} placeholder="Phone" value={newTeamMember.phone} onChangeText={t => setNewTeamMember({...newTeamMember, phone: t})} keyboardType="phone-pad" />
              <TouchableOpacity style={styles.modalButton} onPress={addTeamMember}>
                <Text style={styles.modalButtonText}>Add Member</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Add Location Modal */}
      <Modal visible={showLocationModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.smallModal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Location</Text>
              <TouchableOpacity onPress={() => setShowLocationModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              <TextInput style={styles.modalInput} placeholder="Location Name *" value={newLocation.name} onChangeText={t => setNewLocation({...newLocation, name: t})} />
              <TextInput style={styles.modalInput} placeholder="Address *" value={newLocation.address} onChangeText={t => setNewLocation({...newLocation, address: t})} multiline />
              <TextInput style={styles.modalInput} placeholder="Type (Headquarters/Regional Office)" value={newLocation.type} onChangeText={t => setNewLocation({...newLocation, type: t})} />
              <TextInput style={styles.modalInput} placeholder="Number of employees" value={newLocation.employees} onChangeText={t => setNewLocation({...newLocation, employees: t})} keyboardType="numeric" />
              <TouchableOpacity style={styles.modalButton} onPress={addLocation}>
                <Text style={styles.modalButtonText}>Add Location</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Add Award Modal */}
      <Modal visible={showAwardModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.smallModal}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Award</Text>
              <TouchableOpacity onPress={() => setShowAwardModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalBody}>
              <TextInput style={styles.modalInput} placeholder="Award Title *" value={newAward.title} onChangeText={t => setNewAward({...newAward, title: t})} />
              <TextInput style={styles.modalInput} placeholder="Organization *" value={newAward.organization} onChangeText={t => setNewAward({...newAward, organization: t})} />
              <TextInput style={styles.modalInput} placeholder="Year" value={newAward.year} onChangeText={t => setNewAward({...newAward, year: t})} keyboardType="numeric" />
              <TouchableOpacity style={styles.modalButton} onPress={addAward}>
                <Text style={styles.modalButtonText}>Add Award</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>


      {/* Add Event Modal */}
      <Modal visible={showEventModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.editModalContainer, { height: height * 0.85 }]}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Company Event</Text>
              <TouchableOpacity onPress={() => { setShowEventModal(false); setNewEvent({ title: '', description: '', date: new Date().toISOString().split('T')[0], location: '', type: 'Meeting', media: [] }); }}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ flex: 1 }}>
              <View style={styles.modalBody}>
                <TextInput style={styles.modalInput} placeholder="Event Title *" value={newEvent.title} onChangeText={t => setNewEvent({...newEvent, title: t})} />
                <TextInput style={styles.modalInput} placeholder="Date (YYYY-MM-DD)" value={newEvent.date} onChangeText={t => setNewEvent({...newEvent, date: t})} />
                <TextInput style={styles.modalInput} placeholder="Location" value={newEvent.location} onChangeText={t => setNewEvent({...newEvent, location: t})} />
                <TextInput style={styles.modalInput} placeholder="Type (Meeting/Conference/Workshop/Social)" value={newEvent.type} onChangeText={t => setNewEvent({...newEvent, type: t})} />
                <TextInput style={[styles.modalInput, { height: 80, textAlignVertical: 'top' }]} placeholder="Description" value={newEvent.description} onChangeText={t => setNewEvent({...newEvent, description: t})} multiline />

                {/* Media section */}
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0F172A', marginTop: 8, marginBottom: 12 }}>Photos & Videos</Text>

                {/* Media thumbnails */}
                {(newEvent.media || []).length > 0 && (
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 12 }}>
                    {newEvent.media.map((m, i) => (
                      <View key={i} style={{ marginRight: 8, position: 'relative' }}>
                        {m.type === 'video' ? (
                          <View style={{ width: 100, height: 100, borderRadius: 10, backgroundColor: '#000', alignItems: 'center', justifyContent: 'center' }}>
                            <Ionicons name="play-circle" size={32} color="#fff" />
                            <Text style={{ color: '#fff', fontSize: 9, marginTop: 2 }}>Video</Text>
                          </View>
                        ) : (
                          <Image source={{ uri: m.base64 || m.uri }} style={{ width: 100, height: 100, borderRadius: 10 }} resizeMode="cover" />
                        )}
                        <TouchableOpacity
                          onPress={() => removeEventMedia(i)}
                          style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11, backgroundColor: '#EF4444', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Ionicons name="close" size={14} color="#fff" />
                        </TouchableOpacity>
                      </View>
                    ))}
                  </ScrollView>
                )}

                {/* Media picker buttons */}
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 16 }}>
                  <TouchableOpacity
                    disabled={eventMediaLoading}
                    style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 12, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}
                    onPress={() => pickEventMedia('photo')}
                  >
                    {eventMediaLoading ? <ActivityIndicator size={16} color="#0e4e96" /> : <Ionicons name="images" size={18} color="#0e4e96" />}
                    <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 13 }}>Photos</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    disabled={eventMediaLoading}
                    style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 12, gap: 8, borderWidth: 1, borderColor: '#8B5CF6' }}
                    onPress={() => pickEventMedia('video')}
                  >
                    <Ionicons name="videocam" size={18} color="#8B5CF6" />
                    <Text style={{ color: '#8B5CF6', fontWeight: '600', fontSize: 13 }}>Videos</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    disabled={eventMediaLoading}
                    style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 12, gap: 8, borderWidth: 1, borderColor: '#10B981' }}
                    onPress={takeEventPhoto}
                  >
                    <Ionicons name="camera" size={18} color="#10B981" />
                    <Text style={{ color: '#10B981', fontWeight: '600', fontSize: 13 }}>Camera</Text>
                  </TouchableOpacity>
                </View>

                <Text style={{ fontSize: 11, color: '#94A3B8', marginBottom: 16, textAlign: 'center' }}>
                  {(newEvent.media || []).length} file{(newEvent.media || []).length !== 1 ? 's' : ''} selected · Photos up to 2MB are embedded, larger files stored as references
                </Text>

                <TouchableOpacity style={styles.modalButton} onPress={addEvent}>
                  <Text style={styles.modalButtonText}>Save Event</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Event Preview Modal */}
      <Modal visible={showEventPreviewModal} animationType="slide" onRequestClose={() => setShowEventPreviewModal(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
            <TouchableOpacity onPress={() => setShowEventPreviewModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <Text style={{ fontSize: 17, fontWeight: '600', color: '#0F172A' }} numberOfLines={1}>{previewEvent?.title || 'Event'}</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity onPress={() => previewEvent && printEvent(previewEvent)}><Ionicons name="print" size={22} color="#0e4e96" /></TouchableOpacity>
              <TouchableOpacity onPress={() => previewEvent && generateEventPDF(previewEvent)} disabled={pdfLoading}>
                {pdfLoading ? <ActivityIndicator size={18} color="#0e4e96" /> : <Ionicons name="document" size={22} color="#0e4e96" />}
              </TouchableOpacity>
            </View>
          </View>
          <ScrollView style={{ flex: 1 }}>
            {/* Media gallery */}
            {(() => {
              const media = Array.isArray(previewEvent?.media) ? previewEvent.media : (typeof previewEvent?.media === 'string' ? (function() { try { return JSON.parse(previewEvent.media); } catch { return []; } })() : []);
              return media.length > 0 ? (
                <ScrollView horizontal pagingEnabled showsHorizontalScrollIndicator style={{ height: 260 }}>
                  {media.map((m, i) => {
                    const src = m.base64 || m.uri;
                    return m.type === 'video' ? (
                      <View key={i} style={{ width: width, height: 260, backgroundColor: '#000', alignItems: 'center', justifyContent: 'center' }}>
                        <Ionicons name="play-circle" size={56} color="#fff" />
                        <Text style={{ color: '#fff', fontSize: 13, marginTop: 8 }}>Video {i + 1}</Text>
                      </View>
                    ) : (
                      <Image key={i} source={{ uri: src }} style={{ width: width, height: 260 }} resizeMode="cover" />
                    );
                  })}
                </ScrollView>
              ) : null;
            })()}

            <View style={{ padding: 20 }}>
              <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#0F172A', marginBottom: 8 }}>{previewEvent?.title}</Text>

              {/* Meta badges */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F8FF', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, gap: 4 }}>
                  <Ionicons name="calendar" size={14} color="#0e4e96" />
                  <Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '500' }}>{previewEvent?.date || 'TBD'}</Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0FFF4', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, gap: 4 }}>
                  <MaterialCommunityIcons name="tag" size={14} color="#10B981" />
                  <Text style={{ fontSize: 12, color: '#10B981', fontWeight: '500' }}>{previewEvent?.type || 'Event'}</Text>
                </View>
                {previewEvent?.location ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#FFF7ED', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, gap: 4 }}>
                    <Ionicons name="location" size={14} color="#F59E0B" />
                    <Text style={{ fontSize: 12, color: '#F59E0B', fontWeight: '500' }}>{previewEvent.location}</Text>
                  </View>
                ) : null}
                {(() => {
                  const mc = Array.isArray(previewEvent?.media) ? previewEvent.media.length : 0;
                  return mc > 0 ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F3FF', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16, gap: 4 }}>
                      <Ionicons name="images" size={14} color="#8B5CF6" />
                      <Text style={{ fontSize: 12, color: '#8B5CF6', fontWeight: '500' }}>{mc} media</Text>
                    </View>
                  ) : null;
                })()}
              </View>

              {previewEvent?.description ? (
                <View style={{ backgroundColor: '#F8FAFC', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                  <Text style={{ fontSize: 13, color: '#334155', lineHeight: 20 }}>{previewEvent.description}</Text>
                </View>
              ) : null}

              {/* Company info */}
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, padding: 12, marginBottom: 16, gap: 10 }}>
                {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 36, height: 36, borderRadius: 18 }} /> : <MaterialCommunityIcons name="office-building" size={24} color="#0e4e96" />}
                <View>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: '#0e4e96' }}>{companyProfile?.company_name || companyProfile?.name || 'Company'}</Text>
                  <Text style={{ fontSize: 11, color: '#64748B' }}>{companyProfile?.industry || ''}</Text>
                </View>
              </View>

              {/* Media grid */}
              {(() => {
                const media = Array.isArray(previewEvent?.media) ? previewEvent.media : [];
                return media.length > 0 ? (
                  <View style={{ marginBottom: 20 }}>
                    <Text style={{ fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 10 }}>Gallery ({media.length})</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                      {media.map((m, i) => {
                        const src = m.base64 || m.uri;
                        const sz = (width - 56) / 3;
                        return m.type === 'video' ? (
                          <View key={i} style={{ width: sz, height: sz, borderRadius: 10, backgroundColor: '#000', alignItems: 'center', justifyContent: 'center' }}>
                            <Ionicons name="play-circle" size={28} color="#fff" />
                          </View>
                        ) : (
                          <Image key={i} source={{ uri: src }} style={{ width: sz, height: sz, borderRadius: 10 }} resizeMode="cover" />
                        );
                      })}
                    </View>
                  </View>
                ) : null;
              })()}

              <View style={{ flexDirection: 'row', gap: 10, marginBottom: 40 }}>
                <TouchableOpacity onPress={() => previewEvent && generateEventPDF(previewEvent)} disabled={pdfLoading} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 13, gap: 8 }}>
                  {pdfLoading ? <ActivityIndicator size={14} color="#fff" /> : <Ionicons name="document" size={16} color="#fff" />}
                  <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Export PDF</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => previewEvent && printEvent(previewEvent)} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 13, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
                  <Ionicons name="print" size={16} color="#0e4e96" />
                  <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 14 }}>Print</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Profile Preview Modal */}
      <Modal visible={showPreviewModal} animationType="slide" onRequestClose={() => setShowPreviewModal(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
            <TouchableOpacity onPress={() => setShowPreviewModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <Text style={{ fontSize: 17, fontWeight: '600', color: '#0F172A' }}>Company Preview</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity onPress={printProfile}><Ionicons name="print" size={22} color="#0e4e96" /></TouchableOpacity>
              <TouchableOpacity onPress={generatePDF} disabled={pdfLoading}>
                {pdfLoading ? <ActivityIndicator size={18} color="#0e4e96" /> : <Ionicons name="document" size={22} color="#0e4e96" />}
              </TouchableOpacity>
              <TouchableOpacity onPress={shareProfileText}><Ionicons name="share-outline" size={22} color="#0e4e96" /></TouchableOpacity>
            </View>
          </View>
          <ScrollView style={{ flex: 1, padding: 20 }}>
            <View style={{ alignItems: 'center', marginBottom: 24 }}>
              {companyLogo ? (
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 80, height: 80, borderRadius: 40, marginBottom: 12 }} />
              ) : (
                <View style={[styles.logoPlaceholder, { marginBottom: 12 }]}>
                  <MaterialCommunityIcons name="office-building" size={36} color="#0e4e96" />
                </View>
              )}
              <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#0e4e96', textAlign: 'center' }}>{companyProfile?.company_name || companyProfile?.name || 'Company'}</Text>
              {companyProfile?.industry ? <Text style={{ fontSize: 14, color: '#64748B', marginTop: 4 }}>{companyProfile.industry}</Text> : null}
              {companyProfile?.headquarters ? <Text style={{ fontSize: 13, color: '#94A3B8', marginTop: 2 }}>{companyProfile.headquarters}</Text> : null}
            </View>
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 20 }}>
              {[
                { val: safeGetNumber(businessStats?.employeeCount || companyProfile?.employee_count || companyProfile?.employees, 0), lab: 'Employees' },
                { val: safeGetNumber(businessStats?.activeProjects, 0), lab: 'Projects' },
                { val: safeGetNumber(businessStats?.totalClients, 0), lab: 'Clients' },
                { val: formatCurrency(businessStats?.totalRevenue), lab: 'Revenue' },
              ].map((s, i) => (
                <View key={i} style={{ flex: 1, backgroundColor: '#F0F8FF', borderRadius: 10, padding: 12, alignItems: 'center' }}>
                  <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#0e4e96' }}>{s.val}</Text>
                  <Text style={{ fontSize: 10, color: '#64748B', marginTop: 2 }}>{s.lab}</Text>
                </View>
              ))}
            </View>
            {companyProfile?.description ? (
              <View style={{ backgroundColor: '#F8FAFC', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <Text style={{ fontSize: 11, color: '#94A3B8', marginBottom: 4 }}>About</Text>
                <Text style={{ fontSize: 13, color: '#334155', lineHeight: 19 }}>{companyProfile.description}</Text>
              </View>
            ) : null}
            <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#E2E8F0' }}>
              <Text style={{ fontSize: 15, fontWeight: '600', color: '#0e4e96', marginBottom: 10 }}>Contact</Text>
              {[
                { ic: 'mail', lb: 'Email', vl: companyProfile?.email },
                { ic: 'call', lb: 'Phone', vl: companyProfile?.phone },
                { ic: 'globe', lb: 'Website', vl: companyProfile?.website },
                { ic: 'location', lb: 'HQ', vl: companyProfile?.headquarters },
              ].filter(r => r.vl).map((r, i) => (
                <View key={i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 7, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                  <Ionicons name={r.ic} size={16} color="#0e4e96" style={{ marginRight: 10 }} />
                  <View><Text style={{ fontSize: 10, color: '#94A3B8' }}>{r.lb}</Text><Text style={{ fontSize: 13, color: '#334155', fontWeight: '500' }}>{r.vl}</Text></View>
                </View>
              ))}
            </View>
            {(companyProfile?.vat_number || companyProfile?.registration_number || companyProfile?.legal_name) ? (
              <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0e4e96', marginBottom: 10 }}>Legal</Text>
                {companyProfile.vat_number ? <View style={{ paddingVertical: 5 }}><Text style={{ fontSize: 10, color: '#94A3B8' }}>VAT Number</Text><Text style={{ fontSize: 13, color: '#334155', fontWeight: '500' }}>{companyProfile.vat_number}</Text></View> : null}
                {companyProfile.registration_number ? <View style={{ paddingVertical: 5 }}><Text style={{ fontSize: 10, color: '#94A3B8' }}>Registration</Text><Text style={{ fontSize: 13, color: '#334155', fontWeight: '500' }}>{companyProfile.registration_number}</Text></View> : null}
                {companyProfile.legal_name ? <View style={{ paddingVertical: 5 }}><Text style={{ fontSize: 10, color: '#94A3B8' }}>Legal Name</Text><Text style={{ fontSize: 13, color: '#334155', fontWeight: '500' }}>{companyProfile.legal_name}</Text></View> : null}
              </View>
            ) : null}
            {companyProfile?.bank_name ? (
              <View style={{ backgroundColor: '#F0F8FF', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0e4e96', marginBottom: 10 }}>Banking</Text>
                {[{ l: 'Bank', v: companyProfile.bank_name }, { l: 'Account', v: companyProfile.bank_account }, { l: 'Sort Code', v: companyProfile.bank_sort_code }].filter(r => r.v).map((r, i) => (
                  <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5, borderBottomWidth: 1, borderBottomColor: '#DCE8F8' }}>
                    <Text style={{ fontSize: 12, color: '#64748B' }}>{r.l}</Text><Text style={{ fontSize: 12, color: '#0e4e96', fontWeight: '600' }}>{r.v}</Text>
                  </View>
                ))}
              </View>
            ) : null}
            {documents.length > 0 ? (
              <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ fontSize: 15, fontWeight: '600', color: '#0e4e96', marginBottom: 10 }}>Documents ({documents.length})</Text>
                {documents.map((d, i) => (
                  <View key={d.id || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                    <MaterialCommunityIcons name="file-document" size={18} color="#0e4e96" style={{ marginRight: 10 }} />
                    <View style={{ flex: 1 }}><Text style={{ fontSize: 13, fontWeight: '500', color: '#334155' }}>{d.title}</Text><Text style={{ fontSize: 10, color: '#94A3B8' }}>{d.type || 'Document'}</Text></View>
                  </View>
                ))}
              </View>
            ) : null}
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 40 }}>
              <TouchableOpacity onPress={generatePDF} disabled={pdfLoading} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 13, gap: 8 }}>
                {pdfLoading ? <ActivityIndicator size={14} color="#fff" /> : <Ionicons name="document" size={16} color="#fff" />}
                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Export PDF</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={printProfile} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 13, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
                <Ionicons name="print" size={16} color="#0e4e96" />
                <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 14 }}>Print</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>

      {/* Document Preview Modal */}
      <Modal visible={showDocPreviewModal} animationType="slide" onRequestClose={() => setShowDocPreviewModal(false)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
            <TouchableOpacity onPress={() => setShowDocPreviewModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
            <Text style={{ fontSize: 17, fontWeight: '600', color: '#0F172A' }} numberOfLines={1}>{previewDocument?.title || 'Document'}</Text>
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <TouchableOpacity onPress={() => previewDocument && printDocument(previewDocument)}><Ionicons name="print" size={22} color="#0e4e96" /></TouchableOpacity>
              <TouchableOpacity onPress={() => previewDocument && generateDocumentPDF(previewDocument)} disabled={pdfLoading}>
                {pdfLoading ? <ActivityIndicator size={18} color="#0e4e96" /> : <Ionicons name="document" size={22} color="#0e4e96" />}
              </TouchableOpacity>
            </View>
          </View>
          <ScrollView style={{ flex: 1, padding: 20 }}>
            <View style={{ alignItems: 'center', marginBottom: 20 }}>
              <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: '#E0F2FE', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <MaterialCommunityIcons name={previewDocument?.type === 'Contract' ? 'file-sign' : previewDocument?.type === 'Certificate' ? 'certificate' : previewDocument?.type === 'Policy' ? 'shield-check' : 'file-document'} size={28} color="#0e4e96" />
              </View>
              <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#0F172A', textAlign: 'center' }}>{previewDocument?.title}</Text>
              <Text style={{ fontSize: 14, color: '#64748B', marginTop: 4 }}>{previewDocument?.type || 'Document'}</Text>
            </View>
            <View style={{ backgroundColor: '#F8FAFC', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 12 }}>Details</Text>
              {[
                { l: 'Type', v: previewDocument?.type },
                { l: 'Description', v: previewDocument?.description },
                { l: 'Created', v: previewDocument?.created_at ? new Date(previewDocument.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null },
                { l: 'Company', v: companyProfile?.company_name || companyProfile?.name },
                { l: 'File URL', v: previewDocument?.file_url },
              ].filter(r => r.v).map((r, i) => (
                <View key={i} style={{ flexDirection: 'row', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
                  <Text style={{ fontSize: 13, color: '#64748B', width: 100 }}>{r.l}</Text>
                  <Text style={{ fontSize: 13, color: '#0F172A', fontWeight: '500', flex: 1 }}>{r.v}</Text>
                </View>
              ))}
            </View>
            {previewDocument?.content ? (
              <View style={{ backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 8 }}>Content</Text>
                <Text style={{ fontSize: 13, color: '#334155', lineHeight: 20 }}>{previewDocument.content}</Text>
              </View>
            ) : null}
            <View style={{ flexDirection: 'row', gap: 10, marginBottom: 40 }}>
              <TouchableOpacity onPress={() => previewDocument && generateDocumentPDF(previewDocument)} disabled={pdfLoading} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0e4e96', borderRadius: 10, paddingVertical: 13, gap: 8 }}>
                {pdfLoading ? <ActivityIndicator size={14} color="#fff" /> : <Ionicons name="document" size={16} color="#fff" />}
                <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>Export PDF</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => previewDocument && printDocument(previewDocument)} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F0F8FF', borderRadius: 10, paddingVertical: 13, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
                <Ionicons name="print" size={16} color="#0e4e96" />
                <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 14 }}>Print</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

// ---------- Styles ----------
const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#0e4e96' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f5f5f5' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#666' },
  header: { paddingTop: 10, paddingBottom: 15, paddingHorizontal: 20 },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  backButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  headerTitleContainer: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 18, fontWeight: '600', color: '#fff' },
  headerSubtitle: { fontSize: 12, color: 'rgba(255,255,255,0.8)' },
  editButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  content: { flex: 1, backgroundColor: '#f5f5f5' },
  companyHeaderSection: { backgroundColor: '#fff', paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  logoContainer: { marginBottom: 16 },
  logoWrapper: { position: 'relative' },
  companyLogo: { width: 80, height: 80, borderRadius: 40 },
  logoPlaceholder: { width: 80, height: 80, borderRadius: 40, backgroundColor: '#F0F8FF', alignItems: 'center', justifyContent: 'center' },
  uploadingOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: 40, alignItems: 'center', justifyContent: 'center' },
  cameraIcon: { position: 'absolute', bottom: 0, right: 0, width: 24, height: 24, borderRadius: 12, backgroundColor: '#0e4e96', alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: '#fff' },
  companyInfo: { alignItems: 'center' },
  companyName: { fontSize: 24, fontWeight: '600', color: '#0F172A', marginBottom: 4, textAlign: 'center' },
  companyIndustry: { fontSize: 16, color: '#0e4e96', marginBottom: 8 },
  companyDescription: { fontSize: 14, color: '#64748B', textAlign: 'center', lineHeight: 20, marginBottom: 16 },
  companyStats: { flexDirection: 'row', gap: 24 },
  companyStat: { alignItems: 'center' },
  companyStatValue: { fontSize: 18, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  companyStatLabel: { fontSize: 12, color: '#64748B' },
  tabNavigation: { flexDirection: 'row', backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tabButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 14, paddingHorizontal: 16, gap: 6, marginHorizontal: 2 },
  tabButtonActive: { borderBottomWidth: 2, borderBottomColor: '#0e4e96' },
  tabButtonText: { fontSize: 12, color: '#64748B', fontWeight: '500' },
  tabButtonTextActive: { color: '#0e4e96', fontWeight: '600' },
  tabContent: { padding: 20 },
  section: { marginBottom: 24 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  addButton: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#F0F8FF', alignItems: 'center', justifyContent: 'center' },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  statCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, width: (width - 52) / 2, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  statValue: { fontSize: 18, fontWeight: '600', color: '#0F172A', marginTop: 8, marginBottom: 4 },
  statLabel: { fontSize: 12, color: '#64748B', textAlign: 'center' },
  detailsList: { backgroundColor: '#fff', borderRadius: 12, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  detailItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  detailContent: { flex: 1, marginLeft: 12 },
  detailLabel: { fontSize: 12, color: '#64748B', marginBottom: 2 },
  detailValue: { fontSize: 14, fontWeight: '500', color: '#0F172A' },
  specializationsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  specializationChip: { backgroundColor: '#F0F8FF', borderRadius: 16, paddingHorizontal: 12, paddingVertical: 6 },
  specializationText: { fontSize: 12, fontWeight: '500', color: '#0e4e96' },
  certificationsList: { backgroundColor: '#fff', borderRadius: 12, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2 },
  certificationItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  certificationText: { fontSize: 14, fontWeight: '500', color: '#0F172A', marginLeft: 12 },
  teamGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  teamCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, width: (width - 52) / 2, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2, position: 'relative' },
  teamRemove: { position: 'absolute', top: 8, right: 8, zIndex: 1 },
  teamMemberHeader: { position: 'relative', marginBottom: 12 },
  teamAvatarPlaceholder: { width: 60, height: 60, borderRadius: 30, backgroundColor: '#F0F8FF', alignItems: 'center', justifyContent: 'center' },
  teamAvatarInitials: { fontSize: 20, fontWeight: '600', color: '#0e4e96' },
  statusIndicator: { position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, borderRadius: 6, borderWidth: 2, borderColor: '#fff' },
  teamMemberName: { fontSize: 14, fontWeight: '600', color: '#0F172A', marginBottom: 2, textAlign: 'center' },
  teamMemberPosition: { fontSize: 12, color: '#0e4e96', marginBottom: 2, textAlign: 'center' },
  teamMemberDepartment: { fontSize: 10, color: '#64748B', marginBottom: 12, textAlign: 'center' },
  teamMemberActions: { flexDirection: 'row', gap: 8 },
  teamActionButton: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#F0F8FF', alignItems: 'center', justifyContent: 'center' },
  locationsList: { gap: 12 },
  locationCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2, position: 'relative' },
  locationRemove: { position: 'absolute', top: 8, right: 8, zIndex: 1 },
  locationHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  locationIcon: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F0F8FF', alignItems: 'center', justifyContent: 'center', marginRight: 12 },
  locationInfo: { flex: 1 },
  locationName: { fontSize: 16, fontWeight: '600', color: '#0F172A' },
  locationType: { fontSize: 12, color: '#0e4e96' },
  locationEmployees: { fontSize: 12, color: '#64748B' },
  locationAddress: { fontSize: 14, color: '#64748B', lineHeight: 20 },
  awardsList: { gap: 12 },
  awardCard: { backgroundColor: '#fff', borderRadius: 12, padding: 16, flexDirection: 'row', alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 2, position: 'relative' },
  awardRemove: { position: 'absolute', top: 8, right: 8, zIndex: 1 },
  awardIcon: { width: 48, height: 48, borderRadius: 24, backgroundColor: '#FEF3C7', alignItems: 'center', justifyContent: 'center', marginRight: 16 },
  awardInfo: { flex: 1 },
  awardTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 2 },
  awardOrganization: { fontSize: 14, color: '#64748B', marginBottom: 2 },
  awardYear: { fontSize: 12, color: '#F59E0B', fontWeight: '500' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  editModalContainer: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, height: height * 0.9 },
  smallModal: { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: height * 0.6 },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0F172A' },
  modalBody: { padding: 20 },
  modalInput: { backgroundColor: '#F8FAFC', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 12, fontSize: 14, color: '#0F172A', borderWidth: 1, borderColor: '#E2E8F0', marginBottom: 12 },
  modalButton: { backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center', marginTop: 8 },
  modalButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  editModalContent: { flex: 1 },
  editSection: { padding: 20 },
  editSectionTitle: { fontSize: 16, fontWeight: '600', color: '#0F172A', marginBottom: 16 },
  inputGroup: { marginBottom: 16 },
  inputLabel: { fontSize: 14, fontWeight: '500', color: '#0F172A', marginBottom: 8 },
  textInput: { backgroundColor: '#F8FAFC', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 12, fontSize: 14, color: '#0F172A', borderWidth: 1, borderColor: '#E2E8F0' },
  textArea: { height: 80, textAlignVertical: 'top' },
  saveButton: { backgroundColor: '#0e4e96', borderRadius: 8, paddingVertical: 12, alignItems: 'center', margin: 20 },
  saveButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
});